import React, { useEffect, useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getfeetype, getstandardfees, updatestandardfees } from "../../store/slices/fees";
import { toast } from "react-hot-toast";
import { FiHome, FiChevronRight } from "react-icons/fi";
import { fetchStandardSection } from "../../store/slices/getstandardsection";
import { useParams } from "react-router-dom";
import { CiCirclePlus } from "react-icons/ci";
import { MdDeleteOutline } from "react-icons/md";


export default function EditFeeStructure() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();

  const sessions = Array.from({ length: 2 }, (_, index) => {
    const startYear = currentYear - 1 + index;
    const endYear = String(startYear + 1).slice(-2);

    return `${startYear}-${endYear}`;
  });

  useEffect(() => {
    if (!id) return;

    dispatch(getstandardfees({ id }))
      .unwrap()
      .then((res) => {
        const data = res?.data?.[0];
        if (!data) return;

        setSession(data.session);
        setStandardId(data.standardId?._id);

        setFeeDetails(
          data.feeDetails.map((fee) => ({
            feeCategoryId: fee.feeCategoryId?._id,
            amount: fee.amount,
            paymentBreakdown: fee.paymentBreakdown.map((term) => ({
              name: term.name,
              amount: term.amount,
              dueDate: term.dueDate
                ? term.dueDate.split("T")[0]
                : "",
            })),
          }))
        );
      })
      .catch((err) => {
        toast.error(err || "Failed to fetch fee structure");
      });
  }, [dispatch, id]);

  const [session, setSession] = useState("");
  const [standardId, setStandardId] = useState("");
  const [standards, setStandards] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [feeDetails, setFeeDetails] = useState([
    {
      feeCategoryId: "",
      amount: "",
      paymentBreakdown: [
        {
          name: "",
          amount: "",
          dueDate: ""
        },
      ],
    },
  ]);

  useEffect(() => {
    dispatch(fetchStandardSection())
      .unwrap()
      .then((res) => {
        setStandards(res?.data || []);
        console.log(res?.data)
      })
      .catch((err) => {
        toast.error(err || "Failed to fetch standards");
      });

    dispatch(getfeetype())
      .unwrap()
      .then((res) => {
        setFeeTypes(res?.data || []);
      })
      .catch((err) => {
        toast.error(err || "Failed to fetch fee categories");
      });
  }, [dispatch]);

  const addPaymentBreakdown = (feeIndex) => {
    const updated = [...feeDetails];

    updated[feeIndex].paymentBreakdown.push({
      name: "",
      amount: "",
      dueDate: ""
    });

    updated[feeIndex].amount = updated[feeIndex].paymentBreakdown.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0
    );

    setFeeDetails(updated);
  };

  const removePaymentBreakdown = (feeIndex, breakdownIndex) => {
    const updated = [...feeDetails];

    updated[feeIndex].paymentBreakdown.splice(breakdownIndex, 1);

    updated[feeIndex].amount = updated[feeIndex].paymentBreakdown.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0
    );

    setFeeDetails(updated);
  };

  const handleBreakdownChange = (
    feeIndex,
    breakdownIndex,
    field,
    value
  ) => {
    const updated = [...feeDetails];

    updated[feeIndex].paymentBreakdown[breakdownIndex][field] = value;

    // Auto calculate total amount
    const total = updated[feeIndex].paymentBreakdown.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0
    );

    updated[feeIndex].amount = total;

    setFeeDetails(updated);
  };

  const handleFeeChange = (index, field, value) => {
    const updated = [...feeDetails];
    updated[index][field] = value;
    setFeeDetails(updated);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case "session":
        setSession(value);
        break;

      case "standardId":
        setStandardId(value);
        break;

      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!session) newErrors.session = "Please select session";
    if (!standardId) newErrors.standardId = "Please select standard";

    if (feeDetails.length === 0) {
      newErrors.feeDetails = "Please add at least one fee category";
    }
    feeDetails.forEach((item, index) => {
      if (!item.feeCategoryId) {
        newErrors[`feeCategoryId${index}`] = "Select fee category";
      }

      if (item.paymentBreakdown.length === 0) {
        newErrors[`paymentBreakdown${index}`] =
          "Please add at least one payment breakdown";
      }

      const total = item.paymentBreakdown.reduce(
        (sum, breakdown) => sum + (Number(breakdown.amount) || 0),
        0
      );

      if (total <= 0) {
        newErrors[`amount${index}`] =
          "Please enter payment breakdown amounts";
      }

      item.paymentBreakdown.forEach((breakdown, breakdownIndex) => {
        if (!breakdown.name.trim()) {
          newErrors[`name${index}${breakdownIndex}`] =
            "Enter payment name";
        }

        if (!breakdown.amount || Number(breakdown.amount) <= 0) {
          newErrors[`breakdownAmount${index}${breakdownIndex}`] =
            "Enter valid amount";
        }
        if (!breakdown.dueDate) {
          newErrors[`dueDate${index}${breakdownIndex}`] =
            "Select due date";
        }
      });
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Submit
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) return;
    const payload = {
      id,
      session,
      standardId,
      feeDetails: feeDetails.map(item => ({
        feeCategoryId: item.feeCategoryId,
        amount: Number(item.amount),
        paymentBreakdown: item.paymentBreakdown.map((term) => ({
          name: term.name,
          amount: Number(term.amount),
          dueDate: term.dueDate,
        }))
      }))
    };

    dispatch(updatestandardfees(payload))
      .unwrap()
      .then(() => {
        toast.success("Fee type edited successfully");
        navigate(-1);
      })
      .catch((err) => {
        toast.error(err || "Failed to edit fee type");
      });
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">
          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/institute/home">
                <FiHome style={{ color: "white", marginTop: "-4px" }} />
              </Link>
            </div>

            <FiChevronRight className="separator" />

            <Link to="/institute/fee-structure">Fee Structure</Link>

            <FiChevronRight className="separator" />

            <span className="active">Edit Fee Structure</span>
          </div>
        </div>
      </div>
      <div className="acs-card">
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            {/* Session */}
            <Col md={3} sm={12}>
              <Form.Group>
                <Form.Label>Session</Form.Label>
                <Form.Select
                  name="session"
                  value={session}
                  onChange={handleChange}
                >
                  <option value="">Select Session</option>
                  {sessions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
                {errors.session && (
                  <small className="text-danger">{errors.session}</small>
                )}
              </Form.Group>
            </Col>

            {/* Standard */}
            <Col md={3} sm={12}>
              <Form.Group>
                <Form.Label>Standard</Form.Label>
                <Form.Select
                  name="standardId"
                  value={standardId}
                  onChange={handleChange}
                >
                  <option value="">Select Standard</option>
                  {standards.map((item) => (
                    <option
                      key={item.standardId?._id}
                      value={item.standardId?._id}
                    >
                      {item.standardId?.name}
                    </option>
                  ))}
                </Form.Select>

                {errors.standardId && (
                  <small className="text-danger">{errors.standardId}</small>
                )}
              </Form.Group>
            </Col>
          </Row>

          <hr />

          <h6 className="mb-3">Fee Details</h6>
          {feeDetails.map((fee, feeIndex) => (
            <div
              key={feeIndex}
              className="border rounded p-3 mb-4"
            >
              <Row className="g-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Fee Category</Form.Label>

                    <Form.Select
                      value={fee.feeCategoryId}
                      onChange={(e) =>
                        handleFeeChange(
                          feeIndex,
                          "feeCategoryId",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Fee Category</option>

                      {feeTypes.map((item) => (
                        <option
                          key={item._id}
                          value={item._id}
                        >
                          {item.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Total Amount</Form.Label>

                    <Form.Control
                      type="text"
                      value={fee.amount || 0}
                      readOnly
                      style={{
                        background: "#f8f9fa",
                        cursor: "not-allowed",
                      }}
                    />
                  </Form.Group>
                </Col>


              </Row>

              <hr />

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">Payment Breakdown</h6>

                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => addPaymentBreakdown(feeIndex)}
                >
                  <CiCirclePlus size={20} />
                </button>
              </div>

              {fee.paymentBreakdown.map((term, termIndex) => (
                <Row className="g-3 mb-2 align-items-end" key={termIndex}>
                  <Col md={3}>
                    <Form.Control
                      placeholder="Name (Term 1)"
                      value={term.name}
                      onChange={(e) =>
                        handleBreakdownChange(
                          feeIndex,
                          termIndex,
                          "name",
                          e.target.value
                        )
                      }
                    />

                    {errors[`name${feeIndex}${termIndex}`] && (
                      <small className="text-danger">
                        {errors[`name${feeIndex}${termIndex}`]}
                      </small>
                    )}
                  </Col>

                  <Col md={3}>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      placeholder="Amount"
                      value={term.amount}
                      maxLength={5}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");

                        handleBreakdownChange(
                          feeIndex,
                          termIndex,
                          "amount",
                          value
                        );
                      }}
                    />

                    {errors[`breakdownAmount${feeIndex}${termIndex}`] && (
                      <small className="text-danger">
                        {errors[`breakdownAmount${feeIndex}${termIndex}`]}
                      </small>
                    )}
                  </Col>

                  <Col md={3}>
                    <Form.Control
                      type="date"
                      value={term.dueDate}
                      onChange={(e) =>
                        handleBreakdownChange(
                          feeIndex,
                          termIndex,
                          "dueDate",
                          e.target.value
                        )
                      }
                    />

                    {errors[`dueDate${feeIndex}${termIndex}`] && (
                      <small className="text-danger">
                        {errors[`dueDate${feeIndex}${termIndex}`]}
                      </small>
                    )}
                  </Col>

                  <Col md={1}>
                    {fee.paymentBreakdown.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() =>
                          removePaymentBreakdown(
                            feeIndex,
                            termIndex
                          )
                        }
                      >
                        <MdDeleteOutline size={20} />
                      </button>
                    )}
                  </Col>
                </Row>
              ))}


            </div>
          ))}



          <div className="sa-actions mt-5">
            <button
              type="button"
              className="btn-cancel"
              style={{ color: "black" }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>

            <button type="submit" className="btn-submit">
              Submit
            </button>
          </div>
        </Form>
      </div>
    </>
  );
}